/**
 * The ONE place a page_view is emitted (EVENT_CATALOG.md §page_view).
 *
 * Exactly one per pathname transition — not one per render, which is why the
 * effect keys on pathname alone and the exit is emitted from the cleanup.
 *
 * Background time is never counted. Hidden spans are banked into
 * `foregroundMs` at the moment the tab hides (visibilitychange -> hidden);
 * while hidden, `isHidden` is true and no exit path adds a live elapsed
 * segment on top of that banked total. This was previously wrong (fix round
 * 2): the old code added `Date.now() - enteredAt` unconditionally at exit,
 * which — because `enteredAt` was never reset on hide — meant an 8-hour
 * backgrounded tab reported ~8 hours of engaged_ms instead of the seconds
 * actually spent foregrounded. See PageEventObserver.test.tsx for the
 * regression tests (in particular "does not count background time...").
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { beaconFlush, emitPageExit, emitPageView, flushPageEvents } from "./pageEvents";
import { UNKNOWN_ROUTE, resolveRouteTemplate } from "./routeTemplates";

export default function PageEventObserver() {
  const { pathname, state } = useLocation();
  const enteredAt = useRef<number>(Date.now());
  const foregroundMs = useRef<number>(0);
  const isHidden = useRef<boolean>(false);
  // Read during render into a ref, NOT added to the effect's dep array. The
  // effect below keys on pathname alone by design (see the file header); adding
  // `state` would re-fire a page_view whenever navigation state changed without
  // the path changing. A guard sets this flag on the same navigation that
  // changes the path, so by the time the effect runs the ref already holds it.
  const redirectedFromProtected = useRef<boolean>(false);
  redirectedFromProtected.current =
    (state as { redirectedFromProtected?: boolean } | null)?.redirectedFromProtected === true;

  useEffect(() => {
    const template = resolveRouteTemplate(pathname);
    const route = template ?? UNKNOWN_ROUTE;
    emitPageView(route, {
      is_404: template === null,
      // M-03 counts a session as a dead end on either signal. Without this the
      // rate was 404s only, and a user bounced from a paid page to /login —
      // the more actionable dead end of the two — was invisible.
      was_redirected_from_protected: redirectedFromProtected.current,
    });

    // A route can be entered while already hidden (e.g. a background
    // prerender), so seed from the real document state rather than
    // assuming visible.
    isHidden.current = document.visibilityState === "hidden";
    enteredAt.current = Date.now();
    foregroundMs.current = 0;

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        // Guard: some browsers fire visibilitychange more than once for the
        // same transition. Without the guard, a second "hidden" fire would
        // bank the (now-zero) elapsed-since-enteredAt span again, or worse,
        // double count if enteredAt was never advanced.
        if (!isHidden.current) {
          foregroundMs.current += Date.now() - enteredAt.current;
          isHidden.current = true;
        }
      } else if (isHidden.current) {
        enteredAt.current = Date.now();
        isHidden.current = false;
      }
    };
    const elapsedSinceEntered = () => (isHidden.current ? 0 : Date.now() - enteredAt.current);
    const onUnload = () => {
      emitPageExit(route, foregroundMs.current + elapsedSinceEntered(), "close");
      beaconFlush();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onUnload);
    void flushPageEvents();

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onUnload);
      // Never add a live segment while hidden — it was already banked at
      // hide time by onVisibility.
      emitPageExit(route, foregroundMs.current + elapsedSinceEntered(), "navigation");
      void flushPageEvents();
    };
  }, [pathname]);

  return null;
}
